import { useCallback, useEffect, useState } from 'react'
import { sessionHeaders } from './activity'
import { config } from './config'
import { NmcToggle } from './PredictionControls'
import { predictRequestBody } from './predictionRequest'
import { PlanEditor } from './PlanEditor'
import { ModelSelect, NumberField } from './ui'
import { useEditablePlan } from './useEditablePlan'
import { serializePlanForExport, type ExportSheet } from './exportPlan'
import type { BlacklistRule, ModelOption, Params, PredictResponse } from './domain'

// ── Batch export view ─────────────────────────────────────────────────────────

type BatchInputRow = {
  id: string
  modelIdx: number
  nCars: number
}

// A snapshot of one input row taken when the preview is generated.
type BatchPreviewRow = { id: string; model: ModelOption; nCars: number }

// One collapsible model in the preview: loads the model's blacklist, predicts, and
// renders the same editable PlanEditor as the single view. Reports its finalized
// export sheet upward so the parent can build one Excel file from all sections.
function BatchModelSection({ rowId, model, nCars, params, nmcMode, onPlanChange }: {
  rowId: string
  model: ModelOption
  nCars: number
  params: Params
  nmcMode: boolean
  onPlanChange: (id: string, sheet: ExportSheet | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<PredictResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [blacklist, setBlacklist] = useState<BlacklistRule[]>([])

  const modelCodes = model.codes
  const modelKey = modelCodes.slice().sort().join('+')
  const paramsKey = JSON.stringify(params)

  useEffect(() => {
    let live = true
    setResult(null); setError(null)
    onPlanChange(rowId, null)
    ;(async () => {
      try {
        const blRes = await fetch(`${config.apiUrl}/api/blacklist?model_key=${encodeURIComponent(modelKey)}`, { headers: sessionHeaders() })
        const blData = await blRes.json().catch(() => ({}))
        const bl: BlacklistRule[] = blData.rules ?? []
        if (!live) return
        setBlacklist(bl)
        const res = await fetch(`${config.apiUrl}/api/predict`, {
          method: 'POST',
          headers: sessionHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(predictRequestBody(modelCodes, nCars, params, bl)),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.detail ?? `HTTP ${res.status}`)
        }
        const data: PredictResponse = await res.json()
        if (live) setResult(data)
      } catch (err) {
        if (live) setError(String(err))
      }
    })()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelKey, nCars, paramsKey, rowId, onPlanChange])

  const plan = useEditablePlan(result, modelCodes)

  // Push the current finalized sheet upward whenever the edited plan changes.
  useEffect(() => {
    onPlanChange(rowId, result ? serializePlanForExport(result, model.label, plan) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, result, model.label, plan.blocks, plan.lines, plan.planN, plan.allocation, onPlanChange])

  return (
    <div className="border border-[#E5E7EC] bg-white" style={{ borderTop: '2px solid #92A2BD' }}>
      <button
        type="button" onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="text-[10px] text-[#999]">{open ? '▲' : '▼'}</span>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-black">{model.label}</p>
            <p className="text-xs text-[#999]">
              {result
                ? `${plan.planN} cars · ${result.blocks.length} spec blocks`
                : error ? 'Prediction failed' : 'Predicting…'}
              {result && plan.editCount > 0 && ` · ${plan.editCount} edit${plan.editCount !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <span className="flex-shrink-0 text-xs tabular-nums text-[#999]">{model.codes.join(', ')}</span>
      </button>
      {open && (
        <div className="border-t border-[#E5E7EC] px-5 py-4">
          {error
            ? <div className="border border-black bg-bmw-paper p-3 text-sm text-black">{error}</div>
            : !result
              ? <p className="text-sm text-[#999]">Predicting…</p>
              : <PlanEditor result={result} nmcMode={nmcMode} blacklist={blacklist} plan={plan} />}
        </div>
      )}
    </div>
  )
}

export function BatchExportView({ models, params, nmcMode, onNmcModeChange }: {
  models: ModelOption[]; params: Params; nmcMode: boolean; onNmcModeChange: (v: boolean) => void
}) {
  const [rows, setRows] = useState<BatchInputRow[]>([
    { id: crypto.randomUUID(), modelIdx: 0, nCars: 50 },
  ])
  const [preview, setPreview] = useState<BatchPreviewRow[] | null>(null)
  const [plans, setPlans] = useState<Record<string, ExportSheet | null>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function addRow() {
    setRows(r => [...r, { id: crypto.randomUUID(), modelIdx: 0, nCars: 50 }])
  }
  function removeRow(id: string) {
    setRows(r => r.filter(row => row.id !== id))
  }
  function updateRow(id: string, patch: Partial<BatchInputRow>) {
    setRows(r => r.map(row => (row.id === id ? { ...row, ...patch } : row)))
  }

  function generatePreview() {
    if (!models.length) return
    setError(null)
    setPlans({})
    setPreview(rows.map(r => ({ id: r.id, model: models[r.modelIdx], nCars: r.nCars })))
  }
  function backToInputs() {
    setPreview(null)
    setPlans({})
    setError(null)
  }

  const updatePlan = useCallback((id: string, sheet: ExportSheet | null) => {
    setPlans(p => ({ ...p, [id]: sheet }))
  }, [])

  const readySheets = preview ? preview.map(p => plans[p.id]).filter((s): s is ExportSheet => !!s) : []
  const allReady = preview !== null && readySheets.length === preview.length

  async function handleExport() {
    if (!readySheets.length) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${config.apiUrl}/api/export/excel`, {
        method: 'POST',
        headers: sessionHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ sheets: readySheets, use_nmc: nmcMode }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail ?? `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Specs_${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }

  // ── Input phase ─────────────────────────────────────────────────────────────
  if (preview === null) {
    return (
      <div className="border border-[#E5E7EC] bg-white p-6" style={{ borderTop: '2px solid #92A2BD' }}>
        <p className="mb-4 text-sm text-[#666]">
          Add one row per model variant. Generate a preview to review and edit every model's
          spec blocks before exporting them to a single Excel file.
        </p>

        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_7rem_2rem] gap-2 px-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">Model</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">Cars</span>
            <span />
          </div>
          {rows.map((row, idx) => (
            <div key={row.id} className="grid grid-cols-[1fr_7rem_2rem] items-center gap-2">
              <ModelSelect models={models} value={row.modelIdx} onChange={i => updateRow(row.id, { modelIdx: i })} />
              <NumberField
                min={1} max={500} value={row.nCars}
                onCommit={v => updateRow(row.id, { nCars: v })}
                className="w-full border border-[#CFD3DB] px-3 py-2 text-sm text-black focus:border-bmw-ocean focus:outline-none rounded-[4px]"
              />
              <button
                type="button"
                onClick={() => removeRow(row.id)}
                disabled={rows.length === 1}
                aria-label={`Remove row ${idx + 1}`}
                className="flex h-8 w-8 items-center justify-center text-[#999] hover:bg-[#E8EBF1] hover:text-black disabled:opacity-30"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-[#E5E7EC] pt-4">
          <button type="button" onClick={addRow} className="text-sm text-[#666] hover:text-black">
            + Add model
          </button>
          <div className="flex items-center gap-3">
            <NmcToggle nmcMode={nmcMode} onChange={onNmcModeChange} />
            <button
              type="button"
              onClick={generatePreview}
              disabled={!models.length}
              className="bg-bmw-ocean hover:bg-[#024556] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] rounded-[2px] transition disabled:opacity-40"
            >
              Generate preview
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 border border-black bg-bmw-paper p-3 text-sm text-black">{error}</div>
        )}
      </div>
    )
  }

  // ── Preview + export phase ────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-[#E5E7EC] bg-[#FAFBFC] px-5 py-3" style={{ borderTop: '2px solid #92A2BD' }}>
        <button
          type="button" onClick={backToInputs}
          className="text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black"
        >
          ← Back to inputs
        </button>
        <span className="text-sm text-[#666]">
          {preview.length} model{preview.length !== 1 ? 's' : ''} · {readySheets.length} ready
        </span>
        <div className="ml-auto flex items-center gap-3">
          <NmcToggle nmcMode={nmcMode} onChange={onNmcModeChange} />
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || !allReady || readySheets.length === 0}
            title={!allReady ? 'Waiting for all previews to finish' : undefined}
            className="bg-bmw-ocean hover:bg-[#024556] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] rounded-[2px] transition disabled:opacity-40"
          >
            {loading ? 'Generating…' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-black bg-bmw-paper p-3 text-sm text-black">{error}</div>
      )}

      {preview.map(p => (
        <BatchModelSection
          key={p.id} rowId={p.id} model={p.model} nCars={p.nCars}
          params={params} nmcMode={nmcMode} onPlanChange={updatePlan}
        />
      ))}
    </div>
  )
}

// ── Editable plan (shared by the single view and each batch model) ──────────────
// Single source of truth: a flat list of lines (mirrors the engine Plan) plus
// per-block display metadata (so blocks can be added/removed). Edits are reported
// to the admin Activity trail keyed by the plan's model codes.
